import { Box, Checkbox, FormControl, FormControlLabel, Grid } from "@mui/material";
import { Permission } from "generated-graphql/graphql";

import { PERMISSION_GROUPS } from "../constants/permissionGroups";
import { PermissionCheckboxLabel } from "./PermissionCheckboxLabel";

type PermissionCheckboxGridProps = {
  selectedPermissions: Permission[];
  onChange: (permissions: Permission[]) => void;
  disabled?: boolean;
};

/**
 * Grouped permission checkboxes for the role editor and the emulate-permissions
 * dialog. Clicking a group header toggles every permission in the group.
 */
export function PermissionCheckboxGrid({
  selectedPermissions,
  onChange,
  disabled,
}: PermissionCheckboxGridProps) {
  const togglePermission = (permission: Permission) => {
    const next = selectedPermissions.includes(permission)
      ? selectedPermissions.filter((p) => p !== permission)
      : [...selectedPermissions, permission];
    onChange(next);
  };

  const toggleGroup = (group: string) => {
    const permissions = PERMISSION_GROUPS[group];
    const allSelected = permissions.every((permission) => selectedPermissions.includes(permission));
    const next = allSelected
      ? selectedPermissions.filter((p) => !permissions.includes(p))
      : [...selectedPermissions, ...permissions.filter((p) => !selectedPermissions.includes(p))];
    onChange(next);
  };

  return (
    <Grid container spacing={2}>
      {Object.entries(PERMISSION_GROUPS).map(([group, permissions]) => (
        <Grid size={{ xs: 12, sm: 6 }} key={group}>
          <FormControl component="fieldset" variant="standard">
            <Box
              fontWeight="fontWeightBold"
              mb={1}
              onClick={() => !disabled && toggleGroup(group)}
              sx={{ cursor: disabled ? "default" : "pointer" }}
            >
              {group}
            </Box>
            {permissions.map((permission) => (
              <FormControlLabel
                key={permission}
                control={
                  <Checkbox
                    checked={selectedPermissions.includes(permission)}
                    onChange={() => togglePermission(permission)}
                    disabled={disabled}
                    sx={{ p: 1 }}
                  />
                }
                sx={{ alignItems: "flex-start" }}
                label={<PermissionCheckboxLabel permission={permission} />}
              />
            ))}
          </FormControl>
        </Grid>
      ))}
    </Grid>
  );
}
